/**
 * Tela de Pessoas: alunos, leads e contatos num cadastro so, com a ficha do
 * aluno (CPF e endereco) que preenche o contrato.
 *
 * A ficha que o Igor grava pelo WhatsApp chega "a conferir". A Jaque confere
 * com o aluno, corrige o que precisar e confirma; so ficha confirmada e
 * completa vira contrato. Molde visual dos outros modulos: usa o kit de
 * apps/web/src/ui.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EstadoCarregando, EstadoErro, EstadoVazio } from '../../layout/Shell.tsx';
import { useSessao } from '../../sessao.tsx';
import { ErroApi, escrever, ler, mensagemDe } from '../../ui/api.ts';
import {
  Aviso, Barra, BlocoInicio, Botao, Busca, Campo, Formulario, Painel, Selo, Tabela,
  type Coluna, type Tom,
} from '../../ui/componentes.tsx';
import { dataHora, relativo } from '../../ui/formatos.ts';
import type { TelaDeModulo } from '../contrato.ts';
import { blocosDaFicha } from '../registro.ts';

type Pessoa = {
  id: string;
  nome: string;
  telefone: string | null;
  telefone_formatado: string | null;
  email: string | null;
  cpf: string | null;
  cpf_formatado: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  uf: string | null;
  endereco: string;
  rg: string | null;
  data_nascimento: string | null;
  estado_civil: string | null;
  nacionalidade: string | null;
  profissao: string | null;
  observacoes: string | null;
  ficha_status: 'sem_ficha' | 'a_conferir' | 'confirmada';
  ficha_origem: 'usuario' | 'chave_api' | null;
  ficha_faltando: string[];
  ficha_conferida_em: string | null;
  criado_em: string;
  atualizado_em: string;
};

const STATUS: Record<Pessoa['ficha_status'], { rotulo: string; tom: Tom }> = {
  sem_ficha: { rotulo: 'Sem ficha', tom: 'neutro' },
  a_conferir: { rotulo: 'A conferir', tom: 'aviso' },
  confirmada: { rotulo: 'Confirmada', tom: 'ok' },
};

const ROTULO_CAMPO: Record<string, string> = {
  nome: 'nome', cpf: 'CPF', telefone: 'telefone', email: 'e-mail', cep: 'CEP',
  logradouro: 'rua', numero: 'número', bairro: 'bairro', cidade: 'cidade', uf: 'UF',
};

const CAMPOS = [
  'nome', 'telefone', 'email', 'cpf', 'rg', 'data_nascimento', 'estado_civil', 'nacionalidade',
  'profissao', 'cep', 'logradouro', 'numero',
  'complemento', 'bairro', 'cidade', 'uf', 'observacoes',
] as const;
type CampoForm = typeof CAMPOS[number];
type Form = Record<CampoForm, string>;

const vazio = (): Form =>
  Object.fromEntries(CAMPOS.map((c) => [c, ''])) as Form;

const doRegistro = (p: Pessoa): Form => ({
  nome: p.nome,
  telefone: p.telefone_formatado ?? '',
  email: p.email ?? '',
  cpf: p.cpf_formatado ?? '',
  rg: p.rg ?? '',
  data_nascimento: p.data_nascimento ?? '',
  estado_civil: p.estado_civil ?? '',
  nacionalidade: p.nacionalidade ?? '',
  profissao: p.profissao ?? '',
  cep: p.cep !== null ? `${p.cep.slice(0, 5)}-${p.cep.slice(5)}` : '',
  logradouro: p.logradouro ?? '',
  numero: p.numero ?? '',
  complemento: p.complemento ?? '',
  bairro: p.bairro ?? '',
  cidade: p.cidade ?? '',
  uf: p.uf ?? '',
  observacoes: p.observacoes ?? '',
});

function usePermissao(codigo: string): boolean {
  const { estado } = useSessao();
  return estado.fase === 'dentro' && estado.eu.permissoes.includes(codigo);
}

/**
 * O que os outros modulos mostram dentro da ficha (os contratos do aluno, as
 * matriculas), por `blocosDaFicha`: Pessoas nao importa modulo nenhum.
 */
function BlocosDeOutrosModulos({ pessoa }: { pessoa: Pessoa }) {
  const { estado } = useSessao();
  const eu = estado.fase === 'dentro' ? estado.eu : null;
  const blocos = blocosDaFicha().filter((b) => eu !== null && b.modulo !== 'pessoas'
    && eu.modulos.includes(b.modulo)
    && (b.permissao === undefined || eu.permissoes.includes(b.permissao)));
  if (blocos.length === 0) return null;
  return (
    <div className="pessoas__blocos">
      {blocos.map((b) => (
        <b.conteudo key={`${b.modulo}.${b.id}`} cliente={{ id: pessoa.id, nome: pessoa.nome }} />
      ))}
    </div>
  );
}

function SeloFicha({ p }: { p: Pick<Pessoa, 'ficha_status'> }) {
  const s = STATUS[p.ficha_status];
  return <Selo tom={s.tom}>{s.rotulo}</Selo>;
}

/** Painel de cadastro e ficha. `pessoa` nulo = nova pessoa. */
function PainelPessoa({ pessoa, aberto, aoFechar }: {
  pessoa: Pessoa | null; aberto: boolean; aoFechar: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(() => (pessoa !== null ? doRegistro(pessoa) : vazio()));
  const [erro, setErro] = useState<ErroApi | Error | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const podeEditar = usePermissao(pessoa === null ? 'pessoas.criar' : 'pessoas.editar');
  const podeConferir = usePermissao('pessoas.conferir_ficha');
  const podeExcluir = usePermissao('pessoas.excluir');

  const atualizar = () => qc.invalidateQueries({ queryKey: ['pessoas'] });

  const salvar = useMutation({
    mutationFn: () => {
      // vazio vira null: apaga o campo; o servidor normaliza o resto
      const dados = Object.fromEntries(CAMPOS.map((c) => [c, form[c].trim() === '' ? null : form[c]]));
      return pessoa === null
        ? escrever<Pessoa>('pessoas.criar', { ...dados, nome: form.nome })
        : escrever<Pessoa>('pessoas.editar', { id: pessoa.id, ...dados, nome: form.nome });
    },
    onSuccess: async (p) => {
      setErro(null);
      setAviso(pessoa === null ? 'Pessoa cadastrada.' : 'Alterações salvas.');
      await atualizar();
      if (pessoa === null) aoFechar();
      else setForm(doRegistro(p));
    },
    onError: (e) => { setAviso(null); setErro(e as Error); },
  });

  const conferir = useMutation({
    mutationFn: () => escrever<Pessoa>('pessoas.ficha_conferir', { id: pessoa!.id }),
    onSuccess: async () => { await atualizar(); aoFechar(); },
    onError: (e) => { setAviso(null); setErro(e as Error); },
  });

  const excluir = useMutation({
    mutationFn: () => escrever('pessoas.excluir', { id: pessoa!.id }),
    onSuccess: async () => { await atualizar(); aoFechar(); },
    onError: (e) => setErro(e as Error),
  });

  const erroDo = (c: string) => (erro instanceof ErroApi ? erro.doCampo(c) : undefined);
  const mudar = (c: CampoForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [c]: e.target.value }));
  const entrada = (c: CampoForm, extra: Record<string, unknown> = {}) => (id: string) => (
    <input id={id} value={form[c]} onChange={mudar(c)} disabled={!podeEditar} {...extra} />
  );

  const faltando = pessoa?.ficha_faltando ?? [];
  const status = pessoa !== null ? STATUS[pessoa.ficha_status] : null;

  return (
    <Painel
      aberto={aberto} aoFechar={aoFechar}
      titulo={pessoa === null ? 'Nova pessoa' : pessoa.nome}
      {...(pessoa !== null ? {
        subtitulo: pessoa.ficha_status === 'confirmada'
          ? `Ficha confirmada em ${dataHora(pessoa.ficha_conferida_em)}`
          : `Atualizada ${relativo(pessoa.atualizado_em)}`,
      } : {})}
      rodape={(
        <>
          {pessoa !== null && podeExcluir && (
            <Botao variante="perigo" desativado={excluir.isPending}
              aoClicar={() => { if (window.confirm(`Excluir ${pessoa.nome}?`)) excluir.mutate(); }}>
              Excluir
            </Botao>
          )}
          {pessoa !== null && podeConferir && pessoa.ficha_status === 'a_conferir' && (
            <Botao desativado={conferir.isPending || faltando.length > 0}
              titulo={faltando.length > 0 ? 'Complete a ficha antes de confirmar' : 'Confirmar ficha'}
              aoClicar={() => conferir.mutate()}>
              Confirmar ficha
            </Botao>
          )}
          {podeEditar && (
            <Botao variante="primario" desativado={salvar.isPending} aoClicar={() => salvar.mutate()}>
              {salvar.isPending ? 'Salvando' : 'Salvar'}
            </Botao>
          )}
        </>
      )}
    >
      {status !== null && pessoa !== null && (
        <Aviso tom={status.tom}>
          {pessoa.ficha_status === 'a_conferir'
            ? `Ficha ${pessoa.ficha_origem === 'chave_api' ? 'registrada pelo Igor' : 'alterada'} e ainda não conferida.`
              + (faltando.length > 0
                ? ` Falta: ${faltando.map((c) => ROTULO_CAMPO[c] ?? c).join(', ')}.`
                : ' Confira com o aluno e confirme.')
            : pessoa.ficha_status === 'confirmada'
              ? 'Ficha conferida. Mudar nome, CPF, dados pessoais, contato ou endereço devolve para a conferir.'
              : 'Sem ficha de aluno ainda. Preencha CPF e endereço quando for matricular.'}
        </Aviso>
      )}
      {aviso !== null && <Aviso tom="ok">{aviso}</Aviso>}
      {erro !== null && <Aviso tom="erro">{mensagemDe(erro)}</Aviso>}

      <Formulario aoEnviar={() => salvar.mutate()}>
        <Campo rotulo="Nome completo" erro={erroDo('nome')}>{entrada('nome', { autoComplete: 'off' })}</Campo>
        <Campo rotulo="Telefone (WhatsApp)" largura="metade" erro={erroDo('telefone')}>
          {entrada('telefone', { inputMode: 'tel', placeholder: '(11) 98765-4321' })}
        </Campo>
        <Campo rotulo="E-mail" largura="metade" erro={erroDo('email')}>
          {entrada('email', { type: 'email' })}
        </Campo>
        <Campo rotulo="CPF" largura="metade" erro={erroDo('cpf')}>
          {entrada('cpf', { inputMode: 'numeric', placeholder: '000.000.000-00' })}
        </Campo>
        <Campo rotulo="RG" largura="metade" erro={erroDo('rg')}>{entrada('rg')}</Campo>
        <Campo rotulo="Data de nascimento" largura="metade" erro={erroDo('data_nascimento')}>
          {entrada('data_nascimento', { type: 'date' })}
        </Campo>
        <Campo rotulo="Estado civil" largura="metade" erro={erroDo('estado_civil')}>
          {entrada('estado_civil')}
        </Campo>
        <Campo rotulo="Nacionalidade" largura="metade" erro={erroDo('nacionalidade')}>
          {entrada('nacionalidade', { placeholder: 'Brasileira' })}
        </Campo>
        <Campo rotulo="Profissão" largura="metade" erro={erroDo('profissao')}>
          {entrada('profissao', { placeholder: 'Fisioterapeuta' })}
        </Campo>
        <Campo rotulo="CEP" largura="terco" erro={erroDo('cep')}>
          {entrada('cep', { inputMode: 'numeric', placeholder: '00000-000' })}
        </Campo>
        <Campo rotulo="Rua" largura="terco" erro={erroDo('logradouro')}>{entrada('logradouro')}</Campo>
        <Campo rotulo="Número" largura="terco" erro={erroDo('numero')}>{entrada('numero')}</Campo>
        <Campo rotulo="Complemento" largura="metade" erro={erroDo('complemento')}>{entrada('complemento')}</Campo>
        <Campo rotulo="Bairro" largura="metade" erro={erroDo('bairro')}>{entrada('bairro')}</Campo>
        <Campo rotulo="Cidade" largura="metade" erro={erroDo('cidade')}>{entrada('cidade')}</Campo>
        <Campo rotulo="UF" largura="metade" erro={erroDo('uf')}>
          {entrada('uf', { maxLength: 2, placeholder: 'SP' })}
        </Campo>
        <Campo rotulo="Observações" erro={erroDo('observacoes')}>
          {(id) => (
            <textarea id={id} value={form.observacoes} onChange={mudar('observacoes')}
              disabled={!podeEditar} />
          )}
        </Campo>
        {/* Enter no campo envia */}
        <button type="submit" hidden />
      </Formulario>
      {pessoa !== null && <BlocosDeOutrosModulos pessoa={pessoa} />}
    </Painel>
  );
}

const COLUNAS: readonly Coluna<Pessoa>[] = [
  { id: 'nome', titulo: 'Nome', celula: (p) => <strong>{p.nome}</strong> },
  { id: 'telefone', titulo: 'Telefone', celula: (p) => p.telefone_formatado ?? '' },
  { id: 'email', titulo: 'E-mail', celula: (p) => p.email ?? '', secundaria: true },
  {
    id: 'cidade', titulo: 'Cidade', secundaria: true,
    celula: (p) => [p.cidade, p.uf].filter(Boolean).join('/'),
  },
  { id: 'ficha', titulo: 'Ficha', celula: (p) => <SeloFicha p={p} /> },
];

function ListaPessoas({ soConferir }: { soConferir: boolean }) {
  const [termo, setTermo] = useState('');
  const [aberta, setAberta] = useState<Pessoa | null | 'nova'>(null);
  const podeCriar = usePermissao('pessoas.criar');
  // ?id=<uuid> abre a ficha direto (link de tarefa, contrato, lead)
  const [busca, setBusca] = useSearchParams();
  const idPedido = busca.get('id');
  useEffect(() => {
    if (idPedido === null) return;
    void ler<Pessoa>('pessoas.obter', { id: idPedido }).then(setAberta).catch(() => {});
  }, [idPedido]);
  const fechar = () => {
    setAberta(null);
    if (idPedido !== null) {
      const resto = new URLSearchParams(busca);
      resto.delete('id');
      setBusca(resto, { replace: true });
    }
  };
  const q = useQuery({
    queryKey: ['pessoas', soConferir ? 'a_conferir' : 'todas', termo],
    queryFn: () => ler<{ itens: Pessoa[] }>('pessoas.buscar', {
      termo, limite: 200, ...(soConferir ? { ficha_status: 'a_conferir' } : {}),
    }),
  });

  return (
    <>
      <Barra
        acoes={podeCriar && !soConferir
          ? <Botao variante="primario" aoClicar={() => setAberta('nova')}>Nova pessoa</Botao>
          : undefined}
      >
        <Busca valor={termo} aoMudar={setTermo} dica="Buscar por nome, telefone, e-mail ou CPF" />
      </Barra>

      {q.isPending && <EstadoCarregando texto="Carregando pessoas" />}
      {q.isError && <EstadoErro titulo="Não foi possível carregar" texto={mensagemDe(q.error)} />}
      {q.isSuccess && q.data.itens.length === 0 && (
        <EstadoVazio
          titulo={soConferir ? 'Nenhuma ficha para conferir' : 'Ninguém encontrado'}
          texto={soConferir
            ? 'Quando o Igor registrar a ficha de um aluno, ela aparece aqui para você conferir.'
            : termo !== '' ? 'Tente outro nome, telefone ou CPF.' : 'Cadastre a primeira pessoa.'}
        />
      )}
      {q.isSuccess && q.data.itens.length > 0 && (
        <Tabela colunas={COLUNAS} linhas={q.data.itens} chave={(p) => p.id}
          aoAbrir={(p) => setAberta(p)} />
      )}

      {aberta !== null && (
        <PainelPessoa key={aberta === 'nova' ? 'nova' : aberta.id}
          pessoa={aberta === 'nova' ? null : aberta} aberto aoFechar={fechar} />
      )}
    </>
  );
}

/** Bloco do Inicio: as fichas que esperam a Jaque. */
function BlocoFichas() {
  const q = useQuery({
    queryKey: ['pessoas', 'a_conferir', 'inicio'],
    queryFn: () => ler<{ itens: Pessoa[] }>('pessoas.buscar', { ficha_status: 'a_conferir', limite: 50 }),
  });
  const itens = q.data?.itens ?? [];
  return (
    <BlocoInicio titulo="Fichas a conferir" numero={itens.length}
      tom={itens.length > 0 ? 'aviso' : 'ok'}
      link={{ rotulo: 'Conferir fichas', href: '/pessoas?aba=conferir' }}>
      {q.isPending && <span className="ui-item__detalhe">Carregando</span>}
      {q.isError && <span className="ui-item__detalhe">{mensagemDe(q.error)}</span>}
      {q.isSuccess && itens.length === 0 && (
        <span className="ui-item__detalhe">Nenhuma ficha esperando conferência.</span>
      )}
      {itens.slice(0, 5).map((p) => (
        <div key={p.id} className="ui-item">
          <div className="ui-item__principal">
            <span className="ui-item__nome">{p.nome}</span>
            <span className="ui-item__detalhe">
              {p.ficha_faltando.length > 0 ? `Falta ${p.ficha_faltando.length} campo(s)` : 'Completa'}
            </span>
          </div>
          <SeloFicha p={p} />
        </div>
      ))}
    </BlocoInicio>
  );
}

export const tela: TelaDeModulo = {
  slug: 'pessoas',
  rota: '/pessoas',
  kicker: 'Cadastro único',
  titulo: 'Alunos e pessoas',
  abas: [
    { id: 'pessoas', rotulo: 'Pessoas', conteudo: () => <ListaPessoas soConferir={false} /> },
    { id: 'conferir', rotulo: 'Fichas a conferir', conteudo: () => <ListaPessoas soConferir /> },
  ],
  blocosDoInicio: [{ id: 'fichas', permissao: 'pessoas.ver', conteudo: BlocoFichas }],
  dicionario: { pt: {} },
};
